import React from 'react';
import { Breadcrumb } from '../';
import { useLocation, Link } from 'react-router-dom';
import { ROUTE_CONFIG, DISCOVERY_ROUTE_CONFIG, ROUTES, DISCOVERY_ROUTES } from '../../constants/routes';
import './Breadcrumbs.scss';

// Routes where breadcrumbs should be shown
const INCLUDED_ROUTES = new Set([
    // Discovery dashboard, inventory, rvTools, move groups and apps
    DISCOVERY_ROUTES.dashboard,
    DISCOVERY_ROUTES.inventory,
    DISCOVERY_ROUTES.rvTools,
    DISCOVERY_ROUTES.awsTransform,
    DISCOVERY_ROUTES.moveGroupsAndApps,
]);

/**
 * Breadcrumbs component that shows the navigation path for the current page
 * Automatically rendered in the layout for all pages in INCLUDED_ROUTES
 */
export const Breadcrumbs = () => {
    const location = useLocation();

    // Generate breadcrumb items from current path
    const generateBreadcrumbs = () => {
        const currentPath = location.pathname;
        const breadcrumbItems = [];

        // Find exact route match in both configs
        const routeMatch =
            Object.values(ROUTE_CONFIG).find((route) => route.path === currentPath) ||
            Object.values(DISCOVERY_ROUTE_CONFIG).find((route) => route.path === currentPath);

        if (routeMatch && currentPath !== ROUTES.home) {
            // Format path parts
            let formattedPath = currentPath.startsWith('/') ? currentPath.slice(1) : currentPath;
            const pathParts = formattedPath.split('/');
            const capitalizedParts = pathParts.map((part) => {
                if (!part) return part;
                return part.charAt(0).toUpperCase() + part.slice(1);
            });
            capitalizedParts.forEach((part, index) => {
                const isLast = index === capitalizedParts.length - 1;
                const segmentPath = '/' + pathParts.slice(0, index + 1).join('/');
                const displayText = isLast ? routeMatch.title : part;
                const isValidPath =
                    (Object.values(ROUTES).includes(segmentPath) ||
                        Object.values(DISCOVERY_ROUTES).includes(segmentPath)) &&
                    segmentPath !== DISCOVERY_ROUTES.base;
                let titleContent;
                if (isLast) {
                    titleContent = <span className="breadcrumb-current">{displayText}</span>;
                } else if (isValidPath) {
                    titleContent = (
                        <Link className="breadcrumb-link" to={segmentPath}>
                            {displayText}
                        </Link>
                    );
                } else {
                    titleContent = <span className="breadcrumb-link">{displayText}</span>;
                }
                breadcrumbItems.push({
                    key: segmentPath,
                    title: titleContent,
                });
            });
        }
        return breadcrumbItems;
    };

    // Get breadcrumb items
    const breadcrumbItems = generateBreadcrumbs();

    // Only render breadcrumbs on included routes
    if (!INCLUDED_ROUTES.has(location.pathname)) {
        return null;
    }

    // Don't render if no items
    if (breadcrumbItems.length === 0) {
        return null;
    }

    return (
        <div className="rm-breadcrumbs">
            <Breadcrumb items={breadcrumbItems} separator="/" />
        </div>
    );
};

export default Breadcrumbs;
